import React, { useState, useEffect } from 'react';
import { ApolloProvider, gql, useLazyQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import ConsultantWizardOverlay from '../components/ConsultantAssessment/ConsultantWizardOverlay';

const ADMIN_ENSURE_TARGET = gql`
  query AdminEnsureAssessmentTarget($email: String!, $name: String) {
    adminEnsureAssessmentTarget(email: $email, name: $name) {
      id
      email
      name
      retirement_timeline_bucket
      admin_assessment_snapshot
      user {
        id
        email
        name
      }
    }
  }
`;

function Inner() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [targetId, setTargetId] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [error, setError] = useState(null);
  const [shouldQuery, setShouldQuery] = useState(false);

  const [ensureTarget, { data: queryData, loading: queryLoading, error: queryError }] = useLazyQuery(ADMIN_ENSURE_TARGET, {
    fetchPolicy: 'network-only',
  });

  // Watch for query completion
  useEffect(() => {
    if (!shouldQuery || queryLoading) return;
    
    if (queryError) {
      setError(queryError.message || 'Failed to resolve target');
      setShouldQuery(false);
      return;
    }
    
    if (queryData?.adminEnsureAssessmentTarget) {
      setTargetId(queryData.adminEnsureAssessmentTarget.id);
      setShowWizard(true);
      setShouldQuery(false);
    } else if (queryData && !queryData.adminEnsureAssessmentTarget) {
      setError('Failed to find or create target');
      setShouldQuery(false);
    }
  }, [queryData, queryError, queryLoading, shouldQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setError(null);
    setShouldQuery(true);
    ensureTarget({ 
      variables: { 
        email: email.trim().toLowerCase(), 
        name: name.trim() 
      } 
    });
  };

  if (showWizard && targetId) {
    return (
      <ConsultantWizardOverlay
        inventoryUserId={targetId}
        isOpen={true}
        onClose={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-succession-bg-primary text-succession-text-primary flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-semibold mb-2 text-succession-text-primary">Consultant Assessment</h1>
          <p className="text-succession-text-secondary mb-6">Enter the advisor's email and name to begin the assessment.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-succession-text-primary mb-1">
                Email *
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-succession-border rounded-md bg-succession-bg-primary text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
                placeholder="advisor@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-succession-text-primary mb-1">
                Name (optional)
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-succession-border rounded-md bg-succession-bg-primary text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
                placeholder="Advisor Name"
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={queryLoading || !email.trim()}
              className="w-full px-4 py-2 bg-succession-primary text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-succession-primary-hover"
            >
              {queryLoading ? 'Loading...' : 'Start Assessment'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function ConsultantAssessmentContainer(props) {
  return (
    <ApolloProvider client={client}>
      <Inner />
    </ApolloProvider>
  );
}

